import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Antenna, Gateway } from '@wheere/api-site';
import {
  ControlComponent,
  GeoJSONSourceComponent,
  LayerComponent,
  MapComponent,
  MarkerComponent,
} from '@maplibre/ngx-maplibre-gl';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { TranslocoModule } from '@jsverse/transloco';
import { MapAntennaMarkerComponent, SwitchComponent, WhButtonComponent } from '@wheere-front/shared-ui';
import { Map } from 'maplibre-gl';
import { calculateDynamicPadding, chartCustomColors, getFitBounds } from '@wheere-front/shared-util';
import { SITE_CONFIG_MAP_STATE } from './site-config-map-state';
import { AntennaDraft, AntennaPosition } from './site-configuration.service';

/**
 * Per-gateway link descriptor used to render the gateway iso marker + dashed
 * lines to each antenna + the "RF OUT N" labels at each midpoint. Mirrors the
 * integrator's {@code GatewayAntennaLink} shape so the rendering logic is
 * isomorphic across the two apps.
 */
interface GatewayAntennaLink {
  gatewayId: string;
  gatewayName: string;
  originLng: number;
  originLat: number;
  /** Suppresses the iso marker when the gateway position is implicit (= antenna 0
   *  on a single-antenna gateway). The dashed line + RF OUT label still render. */
  hideGatewayMarker: boolean;
  antennas: Array<{ orderGateway: number; lng: number; lat: number; rfOut: number }>;
}

@Component({
  selector: 'lib-map-preview',
  standalone: true,
  imports: [
    MapComponent,
    ControlComponent,
    SwitchComponent,
    MarkerComponent,
    GeoJSONSourceComponent,
    LayerComponent,
    FastSvgComponent,
    MapAntennaMarkerComponent,
    WhButtonComponent,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      /* Fills the host: the configuration tab gives it the height left by the files card
         (or 40vh when the tab stacks under lg). */
      .preview-inline {
        height: 100%;
        min-height: 220px;
      }
      .preview-fullscreen {
        position: fixed;
        inset: 0;
        z-index: 9999;
        height: 100vh;
        width: 100vw;
        border-radius: 0;
        border: none;
      }
    `,
  ],
  template: `
    <!-- Fullscreen backdrop -->
    @if (fullscreen()) {
      <div class="fixed inset-0 z-[9998] bg-black/40" (click)="fullscreen.set(false)"></div>
    }

    <div
      class="relative overflow-hidden rounded-xl border bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
      [class.preview-inline]="!fullscreen()"
      [class.preview-fullscreen]="fullscreen()">
      <!-- Header — title, gateway-overlay toggle, fullscreen button. The toggle
           drives the dashed RF OUT lines + iso gateway markers; mirrors the
           integrator's "Show gateway overlay" checkbox so the wiring overlay can
           be hidden when the operator just wants the clean antenna view. -->
      <div
        class="absolute left-0 right-0 top-0 z-10 flex items-center justify-between gap-3 px-4 py-2.5 backdrop-blur-sm"
        [class]="fullscreen() ? 'bg-white dark:bg-slate-800' : 'bg-white/90 dark:bg-slate-800/90'"
        [class.shadow-sm]="fullscreen()">
        <span class="text-sm font-semibold text-gray-700 dark:text-slate-200"
          ><i class="pi pi-map mr-1.5"></i>{{ 'siteConfiguration.map.preview' | transloco }}</span
        >
        <div class="flex items-center gap-2">
          <label
            class="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
            [title]="'siteConfiguration.map.rfOutWiringHint' | transloco">
            <input
              type="checkbox"
              class="text-eletricPurple-500 focus:ring-eletricPurple-500 size-3.5 rounded border-gray-300 dark:border-slate-600"
              [checked]="showGatewayOverlay()"
              (change)="showGatewayOverlay.set($any($event.target).checked)" />
            {{ 'siteConfiguration.map.rfOutWiring' | transloco }}
          </label>
          <wh-button variant="ghost" (click)="toggleFullscreen()">
            @if (fullscreen()) {
              <i class="pi pi-times"></i>{{ 'siteConfiguration.map.close' | transloco }}
            } @else {
              <i class="pi pi-expand"></i>{{ 'siteConfiguration.map.fullscreen' | transloco }}
            }
          </wh-button>
        </div>
      </div>

      <!-- Map -->
      <div class="h-full w-full pt-10">
        @defer {
          <mgl-map
            [mapStyle]="mapStyle()"
            [zoom]="[18]"
            [fitBoundsOptions]="fitBoundsOptions()"
            [maxZoom]="19"
            [movingMethod]="'jumpTo'"
            (mapLoad)="onMapLoad($event)"
            class="h-full w-full">
            @if (map()) {
              <mgl-control position="bottom-left">
                <lib-switch [checked]="mapStyleType() !== 'street'" (changed)="onToggleMapStyle()"></lib-switch>
              </mgl-control>

              <!-- Antenna markers — V8 reception points, always shown. Each antenna gets a
                   distinct palette colour (same chartCustomColors cycle as the integrator +
                   the sidebar badge) so gateways/antennas are no longer an indistinct pink
                   blob and a map dot can be tied to its list row by colour. -->
              @for (antenna of allAntennas(); track antenna.id) {
                <lib-map-antenna-marker
                  [antenna]="antenna"
                  [color]="paletteColor(antenna.paletteIndex)"></lib-map-antenna-marker>
              }

              <!-- Per gateway: the dashed RF OUT wiring lines + labels (only when the
                   "RF OUT wiring" toggle is on) plus a black gateway iso marker that is
                   ALWAYS rendered — so a gateway never turns into an antenna-looking dot —
                   and is draggable so the operator can reposition the chassis. -->
              @for (link of gatewayAntennaLinks(); track link.gatewayId) {
                @if (showGatewayOverlay()) {
                  @for (ant of link.antennas; track ant.orderGateway) {
                    <mgl-geojson-source
                      [id]="'gw-ant-line-' + link.gatewayId + '-' + ant.orderGateway"
                      [data]="buildLineFeature(link.originLng, link.originLat, ant.lng, ant.lat)" />
                    <mgl-layer
                      [id]="'gw-ant-line-layer-' + link.gatewayId + '-' + ant.orderGateway"
                      [source]="'gw-ant-line-' + link.gatewayId + '-' + ant.orderGateway"
                      type="line"
                      [paint]="{ 'line-color': '#6B7280', 'line-width': 1.5, 'line-dasharray': [4, 3] }" />
                    <mgl-geojson-source
                      [id]="'gw-ant-label-' + link.gatewayId + '-' + ant.orderGateway"
                      [data]="buildMidpointFeature(link.originLng, link.originLat, ant.lng, ant.lat, ant.rfOut)" />
                    <mgl-layer
                      [id]="'gw-ant-label-layer-' + link.gatewayId + '-' + ant.orderGateway"
                      [source]="'gw-ant-label-' + link.gatewayId + '-' + ant.orderGateway"
                      type="symbol"
                      [layout]="{
                        'text-field': ['get', 'label'],
                        'text-size': 10,
                        'text-anchor': 'center',
                        'text-offset': [0, -0.8],
                      }"
                      [paint]="{ 'text-color': '#374151', 'text-halo-color': '#ffffff', 'text-halo-width': 1 }" />
                  }
                }
                <!-- Gateway iso marker — always shown (distinct black chassis, never
                     confused with an antenna) and draggable to reposition. Hidden only
                     for implicit single-antenna gateways whose position tracks antenna 0
                     (their custom-position selector is off). -->
                @if (!link.hideGatewayMarker) {
                  <mgl-marker
                    [lngLat]="[link.originLng, link.originLat]"
                    [draggable]="draggable()"
                    (markerDragEnd)="onGatewayDragEnd($event, link.gatewayId)">
                    <div
                      class="flex cursor-grab items-center justify-center active:cursor-grabbing"
                      [title]="link.gatewayName">
                      <div class="flex h-8 w-14 items-center justify-center text-black">
                        <fast-svg name="gateway-iso" size="100%" />
                      </div>
                    </div>
                  </mgl-marker>
                }
              }
            }
          </mgl-map>
        }
      </div>

      <!-- No data overlay -->
      @if (!hasData()) {
        <div class="absolute inset-0 flex items-center justify-center bg-gray-50 pt-10 dark:bg-slate-900">
          <div class="text-center text-sm text-gray-400 dark:text-slate-500">
            <i class="pi pi-map mb-2 text-2xl"></i>
            <p>{{ 'siteConfiguration.map.empty' | transloco }}</p>
          </div>
        </div>
      }
    </div>
  `,
})
export class MapPreviewComponent {
  @HostListener('document:keydown.escape')
  onEscKey() {
    if (this.fullscreen()) this.toggleFullscreen();
  }

  /**
   * V8 — every site is N gateways × 1..4 antennas. Topology is derived from the
   * inputs themselves:
   *   - {@link gateways} drives gateway markers + serves as the source for the
   *     iso SVG markers (when {@link showGatewayOverlay} is on).
   *   - {@link gatewayAntennas} carries the live draft antennas per gateway —
   *     read from {@link SiteConfigurationService#gatewayAntennas} so position
   *     edits update the lines/labels/antenna markers in real time. Falls back
   *     to {@code gateway.antennas[]} embedded on each gateway when the input
   *     isn't provided.
   *   - {@link antennaPositions} is the legacy flat list, kept for back-compat
   *     (any caller still passing it gets its values rendered as antennas too).
   */
  gateways = input<Gateway[]>([]);
  antennaPositions = input<AntennaPosition[]>([]);
  /** Per-gateway antenna drafts (V8 — replaces the flat antennaPositions input). */
  gatewayAntennas = input<Record<string, AntennaDraft[]>>({});

  /** Ids of gateways that expose a distinct chassis position (multi-antenna, or a
   *  single-antenna gateway whose "custom gateway position" selector is on). Only these
   *  get a draggable gateway marker; implicit single-antenna gateways track antenna 0. */
  customGatewayIds = input<string[]>([]);

  /** Emitted when the operator drops a dragged gateway marker — the parent patches the
   *  gateway position so the draft stays the single source of truth. */
  gatewayDragged = output<{ gatewayId: string; lng: number; lat: number }>();
  /** Marqueurs de gateway déplaçables (faux en lecture seule). */
  draggable = input<boolean>(true);

  /** Gateway overlay toggle — controls dashed RF OUT lines + iso markers. Default
   *  on so the operator immediately sees the wiring relationship; uncheck for a
   *  clean antenna-only view. */
  showGatewayOverlay = signal(true);

  private mapStateService = inject(SITE_CONFIG_MAP_STATE);

  map = signal<Map | undefined>(undefined);
  mapStyleType = this.mapStateService.mapStyleType;
  mapStyle = this.mapStateService.mapStyle;
  fullscreen = signal(false);

  private shouldRecalculateFitBounds = signal(true);
  private boundsFitted = false;

  validGateways = computed(() => {
    return this.gateways().filter(
      g =>
        g.latitude != null &&
        g.longitude != null &&
        g.altitude != null &&
        !isNaN(g.latitude) &&
        !isNaN(g.longitude) &&
        !isNaN(g.altitude)
    );
  });

  /**
   * Per-gateway antennas resolved from the draft input first, then from the
   * gateway's embedded antennas[] as a fallback. Each entry is an array of
   * {@link AntennaDraft} for that gateway.
   */
  private resolvedAntennas = computed(() => {
    const drafts = this.gatewayAntennas() ?? {};
    const out: Record<string, AntennaDraft[]> = {};
    for (const g of this.gateways()) {
      if (!g.id) continue;
      const fromDraft = drafts[g.id];
      if (fromDraft && fromDraft.length > 0) {
        out[g.id] = fromDraft;
      } else if (g.antennas && g.antennas.length > 0) {
        out[g.id] = g.antennas.map(a => ({
          orderGateway: a.orderGateway ?? 0,
          latitude: a.latitude,
          longitude: a.longitude,
          altitude: a.altitude,
          rfOut: (a as { rfOut?: number | null }).rfOut ?? null,
        }));
      }
    }
    return out;
  });

  /** Flat list of every antenna across all gateways, plus the legacy flat input
   *  for any caller still passing {@link antennaPositions}. Drives the antenna
   *  markers + the fit-bounds. */
  allAntennas = computed<Array<Antenna & { paletteIndex: number }>>(() => {
    const out: Array<Antenna & { paletteIndex: number }> = [];
    const resolved = this.resolvedAntennas();
    let globalIdx = 0;
    for (const g of this.gateways()) {
      if (!g.id) continue;
      const ants = resolved[g.id] ?? [];
      for (const a of ants) {
        if (a.latitude == null || a.longitude == null || a.altitude == null) {
          // Still advance the palette index for unpositioned antennas so the colour
          // of a positioned antenna matches its fixed slot in the sidebar list.
          globalIdx++;
          continue;
        }
        out.push({
          id: `${g.id}-${a.orderGateway ?? globalIdx}`,
          orderGateway: a.orderGateway ?? globalIdx,
          latitude: a.latitude,
          longitude: a.longitude,
          altitude: a.altitude,
          paletteIndex: globalIdx,
        } as Antenna & { paletteIndex: number });
        globalIdx++;
      }
    }
    // Legacy flat input fallback (no gateways or gateways without antennas).
    if (out.length === 0) {
      this.antennaPositions().forEach((a, i) => {
        if (a.latitude == null || a.longitude == null || a.altitude == null) return;
        out.push({
          id: `antenna-${i}`,
          orderGateway: i,
          latitude: a.latitude,
          longitude: a.longitude,
          altitude: a.altitude,
          paletteIndex: i,
        } as Antenna & { paletteIndex: number });
      });
    }
    return out;
  });

  /** Cycle through the shared chartCustomColors so a0=pink, a1=purple, a2=yellow, … —
   *  the exact same palette the integrator's site-view-map uses for its markers, and
   *  which the sidebar antenna badges mirror (keyed by the flat antenna index). */
  protected paletteColor(i: number): string {
    return chartCustomColors[i % chartCustomColors.length];
  }

  /**
   * Per-gateway link descriptors driving the dashed RF OUT lines + labels + iso
   * gateway markers. The origin defaults to the gateway's own lat/lng when set,
   * else to the antenna centroid. A single-antenna gateway whose origin equals
   * the antenna's coord gets {@code hideGatewayMarker=true} so we don't draw the
   * iso SVG on top of the antenna marker.
   */
  gatewayAntennaLinks = computed<GatewayAntennaLink[]>(() => {
    const out: GatewayAntennaLink[] = [];
    const resolved = this.resolvedAntennas();
    for (const g of this.gateways()) {
      if (!g.id) continue;
      const ants = (resolved[g.id] ?? []).filter(a => a.latitude != null && a.longitude != null);
      if (ants.length === 0) continue;
      // Implicit single-antenna gateway (custom-position selector off): its chassis IS
      // antenna 0, so we draw NEITHER the dashed RF OUT wiring line NOR a gateway marker —
      // only the antenna dot remains. Skip the whole link entry. A gateway is "custom"
      // (distinct, draggable chassis) when it has >1 antenna or its id is in customGatewayIds.
      const isCustomPosition = ants.length > 1 || this.customGatewayIds().includes(g.id);
      if (!isCustomPosition) continue;
      // Origin: gateway's own coord if set, else antenna centroid.
      const hasOwn = g.latitude != null && g.longitude != null && !isNaN(g.latitude) && !isNaN(g.longitude);
      const originLat = hasOwn ? g.latitude! : ants.reduce((s, a) => s + (a.latitude as number), 0) / ants.length;
      const originLng = hasOwn ? g.longitude! : ants.reduce((s, a) => s + (a.longitude as number), 0) / ants.length;
      out.push({
        gatewayId: g.id,
        gatewayName: g.name ?? '',
        originLat,
        originLng,
        hideGatewayMarker: false,
        antennas: ants.map((a, i) => ({
          orderGateway: a.orderGateway ?? i,
          lat: a.latitude as number,
          lng: a.longitude as number,
          rfOut: a.rfOut != null ? a.rfOut : (a.orderGateway ?? i),
        })),
      });
    }
    return out;
  });

  hasData = computed(() => this.validGateways().length > 0 || this.allAntennas().length > 0);

  fitBounds = computed(() => {
    const coords: { lng: number; lat: number }[] = [];
    // validGateways / allAntennas ne gardent que des points complets (filtrés non nuls).
    for (const g of this.validGateways()) {
      coords.push({ lat: g.latitude!, lng: g.longitude! });
    }
    for (const a of this.allAntennas()) {
      coords.push({ lat: a.latitude!, lng: a.longitude! });
    }
    return coords.length > 0 ? getFitBounds(coords) : undefined;
  });

  fitBoundsOptions = computed(() => ({
    padding: this.fitBounds() ? calculateDynamicPadding(this.fitBounds()!) : 20,
    animate: false,
  }));

  constructor() {
    effect(
      () => {
        const mapInstance = this.map();
        const bounds = this.fitBounds();
        if (mapInstance && bounds && this.shouldRecalculateFitBounds() && !this.boundsFitted) {
          mapInstance.fitBounds(bounds, this.fitBoundsOptions());
          this.shouldRecalculateFitBounds.set(false);
          this.boundsFitted = true;
        }
      },
      { allowSignalWrites: true }
    );
  }

  onMapLoad(mapInstance: Map | undefined) {
    if (!mapInstance) return;
    this.map.set(mapInstance);
    const bounds = this.fitBounds();
    if (bounds) {
      mapInstance.fitBounds(bounds, this.fitBoundsOptions());
      this.boundsFitted = true;
    }
  }

  toggleFullscreen() {
    this.fullscreen.set(!this.fullscreen());
    setTimeout(() => {
      this.map()?.resize();
      const bounds = this.fitBounds();
      if (bounds) this.map()?.fitBounds(bounds, this.fitBoundsOptions());
    }, 50);
  }

  onToggleMapStyle() {
    this.mapStateService.onToggleMapStyle();
  }

  /** Extract the dropped LngLat from ngx-maplibre-gl's (markerDragEnd) payload — the
   *  wrapper emits the MarkerComponent whose getLngLat() returns the drop point — and
   *  bubble it up so the parent updates the gateway position (the marker re-binds
   *  through the [lngLat] input on the next render). */
  onGatewayDragEnd(evt: unknown, gatewayId: string): void {
    const marker =
      (evt as { target?: { getLngLat?: () => { lng: number; lat: number } } })?.target ??
      (evt as { getLngLat?: () => { lng: number; lat: number } });
    const lngLat = marker?.getLngLat?.();
    if (!lngLat || !Number.isFinite(lngLat.lng) || !Number.isFinite(lngLat.lat)) return;
    this.gatewayDragged.emit({ gatewayId, lng: lngLat.lng, lat: lngLat.lat });
  }

  /** GeoJSON LineString feature from (lng0,lat0) to (lng1,lat1) — drives the
   *  dashed gw→antenna lines. */
  buildLineFeature(lng0: number, lat0: number, lng1: number, lat1: number): GeoJSON.Feature {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [
          [lng0, lat0],
          [lng1, lat1],
        ],
      },
    };
  }

  /** GeoJSON Point feature at the midpoint of (lng0,lat0)→(lng1,lat1) carrying
   *  the "RF OUT N" label. */
  buildMidpointFeature(lng0: number, lat0: number, lng1: number, lat1: number, rfOut: number): GeoJSON.Feature {
    return {
      type: 'Feature',
      properties: { label: `RF OUT${rfOut}` },
      geometry: {
        type: 'Point',
        coordinates: [(lng0 + lng1) / 2, (lat0 + lat1) / 2],
      },
    };
  }
}
